// © Microsoft Corporation. All rights reserved.

import { DynamicReducer, IReducer } from "../reducers";
import { PerfTimeline, tryGetPerfTimeline } from "@msnews/diagnostics";
import { Subspace, subspace } from "redux-subspace";
import { merge as _merge, set as _set } from "lodash-es";

import { tryGetDataConnector } from "../reducers/GetDataConnector";
import { initializeDataConnectorTracker } from "../reducers/DataConnectorTracker";
import { ConnectorConstants } from "../constants/ConnectorConstants";
import { IDataConnector } from "./IDataConnector";
import { IRequestContext } from "@msnews/core";
import { Store } from "redux";

// Inject DataConnectorTracker with tryGetDataConnector function to avoid a dependency cycle.
initializeDataConnectorTracker(tryGetDataConnector);

/**
 * Base class for data connectors.
 * A Data connector is a the base for all connectors in a msnews-experiences-redux application. The data connector has
 * the methods and properties that manage a state object, which can either be a sub-state within the app, or the full
 * application state tree
 * @class
 */
export class DataConnector<TConfig, TState, TParentState> implements IDataConnector<TState, TParentState> {
    /**
     * The Redux subspace store for this connector
     * @type {Subspace<TState>}
     */
    protected _subspace: Subspace<TState, TParentState>;

    /**
     * The full namespace, including all of the ancestors, in dotted notation:
     * i.e.: parent.child.grandchild
     */
    private _fullNamespace: string;

    /**
     * The perf timeline object for this connector
     */
    private _perfTimeline: PerfTimeline;

    /**
     * The config href for the connector
     */
    private _configHref: string;

    /**
     * Gets the Redux store for this data connector instance, which is a subspace within the parent state
     * @property
     * @returns {Store<TState>} The state subspace for this connector, which looks like a Redux Store
     */
    public get store(): Store<TState> {
        return this._subspace;
    }

    /**
     * Gets the full namespace for this connector
     * @property
     * @returns {string} The connector's full namespace
     */
    public get fullNamespace(): string {
        return this._fullNamespace;
    }

    /**
     * The perf timeline object for this connector
     */
    public get perfTimeline(): PerfTimeline {
        return this._perfTimeline;
    }

    /**
     * Get the config href for the connector
     */
    public get configHref(): string {
        return this._configHref;
    }
    /**
     * Set the config href for the connector
     */
    public set configHref(href: string) {
        this._configHref = href;
    }

    /**
     * Construct a new Data connector
     * @constructor
     * @param { string } namespace The redux subspace namespace for the connector
     * @param { string } parentNamespace The redux subspace namespace for the parent connector, if it exists
     * @param { DynamicReducer } rootReducer The root reducer for the Application
     * @param { IReducer<TState> } reducer The reducer for this connector instance
     * @param { Store<TParentState>} parentStore The parent store (or redux subspace) that this connector will be a subspace within
     * @param { TConfig } config Param containing the config for the connector
     * @param { IRequestContext } requestContext The request context
     * @param { boolean } isGlobal Optional param to set if the connector should be global or not. Defaults to false
     */
    public constructor(
        public readonly namespace: string,
        public readonly parentNamespace: string,
        public readonly rootReducer: DynamicReducer,
        public readonly reducer: IReducer<TState>,
        parentStore: Store<TParentState>,
        public config: TConfig,
        public requestContext: IRequestContext,
        public readonly isGlobal: boolean = false) {
        // Setup the full namespace. This namespace will be used as the parentNamespace to any connectors created as children of this
        // connector. It is the "dotted" concatenation of the parentNamespace + namespace, which represents the key into the state tree for the child connectors
        this._fullNamespace = (typeof parentNamespace !== "undefined" && parentNamespace !== "") ? parentNamespace + "." + namespace : namespace;

        // setup the sub-spaced store with a state selector that is bound to this connector's instance, and returns the inner state object (TState) for this connector
        this.selectState = this.selectState.bind(this);
        this._subspace = subspace(this.selectState, this.namespace)(parentStore);

        // try to create a perf timeline for this connector
        this._perfTimeline = this.tryGetPerfTimeline(namespace);

        rootReducer.addSubReducerForConnector<TState, TParentState>(this);
    }

    /**
     * Gets the current state of the connector. The connector's state (TState) is the sub-portion of the returned state object with the postfix "State"
     * This is because the overall subspace state for the connector will contain the children states as well
     * @method
     * @returns {TState} The current state of the component.
     */
    public getCurrentState(): TState {
        // Get the subspace state for this connector. It will be something like:
        // {  _@STATE@_ : { TState }}, where the subspace selector will already have been used to get the namespaced substate
        const wrappedState: TState = this.store.getState();

        // Get the currentState, that will be returned. By default it wil be just the state for this controller
        const connectorState = wrappedState[ConnectorConstants.connectorStateKey];

        // If the connector's state is not global, then check to see if there are any global connectors it needs to be merged with
        // If the connector is global, then its state is just returned, otherwise we would have an infinte loop merging a global connectors's
        // state with itself.
        if (!this.isGlobal) {
            // Get the list of global connectors
            const globalConnectors = this.rootReducer.globalConnectors();

            // See if there are any global connectors that need to be merged in.
            if (globalConnectors.length !== 0) {
                // For each global connector, merge it's state into the final result, using the global connector's namespace
                // So the resulting state will be:
                // { globalConnectorNamespace: { state }, TSTATE }}
                globalConnectors.forEach(globalConnector => {
                    _set(connectorState, globalConnector.namespace, globalConnector.getCurrentState());
                });
            }
        }
        return connectorState;
    }

    /**
     * The method is called as the application is starting up, components are created.
     * The method can be used by connectors to perform one time initialization functions
     * This method needs to be overridden if your connector has specific startup needs
     * @method
     * @returns void
     */
    public onApplicationStart(): void { /** no-op */ }

    /**
     * The subspace selector that gets the portion of the state tree that is for this connector
     * @param state The parent state object that contains the subspace
     */
    private selectState(state: TParentState): TState {
        return (state[this.namespace]);
    }

    /**
     * Attempts to create the {PerfTimeline} object for this connector
     */
    private tryGetPerfTimeline(namespace: string): PerfTimeline {

        const namespaceParts = namespace.split("_");
        const [connectorType] = namespaceParts;

        // there may not be any instance id -- in which case this would simply be the connector type.
        const instanceId = namespaceParts[namespaceParts.length - 1];

        // get the perf timeline object if applicable to the current experience
        return tryGetPerfTimeline(connectorType, instanceId);
    }
}